import { useState } from 'react';

import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { inFrameDialogProps } from '@/designs-dev/device/inFrameDialog';
import SearchField from '@/components/mui/SearchField';
import {
  DOCUMENT_TAGS,
  type DocumentItem,
  todayLabel,
} from '@/cam-admin/control-panel/fixtures';

const stop = (e: React.MouseEvent) => e.stopPropagation();

const DocumentRow = ({
  doc,
  onView,
  onEdit,
  onDelete,
}: {
  doc: DocumentItem;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const pick = (fn: () => void) => () => {
    setAnchor(null);
    fn();
  };
  return (
    <Card variant='listItem' sx={{ px: 2, py: 1.5 }} onClick={onView}>
      <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
        <Box sx={{ color: 'primary.main', display: 'flex' }}>
          <Icon icon={'file-lines' as never} fontSize={20} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant='body1' noWrap>
            {doc.name}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {doc.size} · Uploaded by {doc.uploadedBy} · {doc.date}
          </Typography>
        </Box>
        <Stack direction='row' spacing={1} onClick={stop}>
          {doc.tags.map((t) => (
            <Chip key={t} label={t} size='small' />
          ))}
        </Stack>
        <Box onClick={stop}>
          <IconButton
            size='small'
            aria-label='Document actions'
            onClick={(e) => setAnchor(e.currentTarget)}
          >
            <Icon icon={'ellipsis-vertical' as never} fontSize={16} />
          </IconButton>
          <Menu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => setAnchor(null)}
          >
            <MenuItem onClick={pick(onView)}>View</MenuItem>
            <MenuItem onClick={pick(onEdit)}>Edit</MenuItem>
            <MenuItem onClick={pick(onDelete)} sx={{ color: 'error.main' }}>
              Delete
            </MenuItem>
          </Menu>
        </Box>
      </Stack>
    </Card>
  );
};

// Upload / edit share one form dialog: name (required), tags, optional note.
const DocumentDialog = ({
  mode,
  initial,
  embedded,
  onClose,
  onSubmit,
}: {
  mode: 'upload' | 'edit';
  initial?: DocumentItem;
  embedded?: boolean;
  onClose: () => void;
  onSubmit: (fields: { name: string; tags: string[]; note?: string }) => void;
}) => {
  const [name, setName] = useState(initial?.name ?? '');
  const [tags, setTags] = useState<string[]>(initial?.tags ?? []);
  const [note, setNote] = useState(initial?.note ?? '');
  const isUpload = mode === 'upload';

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth='xs'
      fullWidth
      {...(embedded ? inFrameDialogProps : {})}
    >
      <DialogTitle>
        <Stack
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          direction='row'
        >
          <Typography variant='h3' color='primary.main'>
            {isUpload ? 'Upload document' : 'Edit document'}
          </Typography>
          <IconButton aria-label='close' onClick={onClose}>
            <Icon icon='xmark' fontSize={18} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          {isUpload && (
            <Typography variant='subtitle1'>
              PDF, DOC, DOCX, JPG or PNG, up to 10MB. Everyone in the care
              circle will be able to see it.
            </Typography>
          )}

          <TextField
            fullWidth
            label='Document name'
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            slotProps={{
              select: {
                multiple: true,
                renderValue: (selected) => (selected as string[]).join(' · '),
              },
            }}
            select
            fullWidth
            label='Tags'
            value={tags}
            onChange={(e) =>
              setTags(
                typeof e.target.value === 'string'
                  ? e.target.value.split(',')
                  : e.target.value,
              )
            }
          >
            {DOCUMENT_TAGS.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label='Add a note (optional)'
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant='outlined' color='primary' onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant='contained'
          color='primary'
          disabled={!name.trim()}
          onClick={() =>
            onSubmit({
              name: name.trim(),
              tags,
              note: note.trim() || undefined,
            })
          }
        >
          {isUpload ? 'Upload' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Documents: search and tag filter, upload, view/edit/delete via the row
// menu. Visible to the whole care circle once uploaded (documents spec).
const DocumentsTab = ({
  documents,
  embedded,
  initialUpload,
  onChange,
  onToast,
}: {
  documents: DocumentItem[];
  embedded?: boolean;
  initialUpload?: boolean;
  onChange: (next: DocumentItem[]) => void;
  onToast: (m: string) => void;
}) => {
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [uploadOpen, setUploadOpen] = useState(Boolean(initialUpload));
  const [editing, setEditing] = useState<DocumentItem | null>(null);
  const [deleting, setDeleting] = useState<DocumentItem | null>(null);

  const visible = documents.filter(
    (d) =>
      d.name.toLowerCase().includes(search.toLowerCase()) &&
      (!tagFilter || d.tags.includes(tagFilter)),
  );

  return (
    <Stack spacing={2}>
      {/* Top-align so the search field sits flush with the toolbar top (same
          spacing above it as the other tabs) rather than centred against the
          taller Upload button. */}
      <Stack sx={{ alignItems: 'flex-start' }} direction='row' spacing={1.5}>
        {/* SearchField owns the adornment, the size and the pill. The wrapper
            owns the width. */}
        <Box sx={{ width: 320 }}>
          <SearchField
            placeholder='Search documents'
            value={search}
            onChange={setSearch}
          />
        </Box>
        <TextField
          slotProps={{
            select: {
              displayEmpty: true,
              renderValue: () => tagFilter || 'All tags',
            },
          }}
          select
          size='xsmall'
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
          style={{ width: 180 }}
        >
          <MenuItem value=''>All tags</MenuItem>
          {DOCUMENT_TAGS.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Button
          variant='contained'
          color='primary'
          size='small'
          onClick={() => setUploadOpen(true)}
        >
          Upload document
        </Button>
      </Stack>

      <Stack spacing={1}>
        {visible.map((d) => (
          <DocumentRow
            key={d.id}
            doc={d}
            onView={() =>
              onToast(`Opening ‘${d.name}’ — mocked in the prototype`)
            }
            onEdit={() => setEditing(d)}
            onDelete={() => setDeleting(d)}
          />
        ))}
        {visible.length === 0 && (
          <Typography variant='body1' color='text.secondary'>
            No documents match. Try a different search or clear the tag filter.
          </Typography>
        )}
      </Stack>

      {uploadOpen && (
        <DocumentDialog
          mode='upload'
          embedded={embedded}
          onClose={() => setUploadOpen(false)}
          onSubmit={(fields) => {
            onChange([
              {
                id: `new-d${documents.length + 1}`,
                name: fields.name,
                tags: fields.tags,
                note: fields.note,
                size: '84kb',
                uploadedBy: 'You',
                date: todayLabel(),
              },
              ...documents,
            ]);
            setUploadOpen(false);
            onToast(
              `Uploaded ‘${fields.name}’. The care circle can now see it`,
            );
          }}
        />
      )}

      {editing && (
        <DocumentDialog
          mode='edit'
          initial={editing}
          embedded={embedded}
          onClose={() => setEditing(null)}
          onSubmit={(fields) => {
            onChange(
              documents.map((d) =>
                d.id === editing.id ? { ...d, ...fields } : d,
              ),
            );
            setEditing(null);
            onToast('Document updated');
          }}
        />
      )}

      {/* delete confirm — copy straight from the documents spec */}
      <Dialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        maxWidth='xs'
        fullWidth
        {...(embedded ? inFrameDialogProps : {})}
      >
        <DialogTitle>
          <Stack
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
            direction='row'
          >
            <Typography variant='h3' color='primary.main'>
              Delete document
            </Typography>
            <IconButton aria-label='close' onClick={() => setDeleting(null)}>
              <Icon icon='xmark' fontSize={18} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Typography variant='subtitle1'>
            Are you sure you want to delete ‘{deleting?.name}’? This can’t be
            undone.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            variant='outlined'
            color='primary'
            onClick={() => setDeleting(null)}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={() => {
              onChange(documents.filter((d) => d.id !== deleting?.id));
              onToast('Document deleted');
              setDeleting(null);
            }}
          >
            Confirm and delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default DocumentsTab;
